/**
 * Structure views (M3 plan R13, order `FEATURE_ORDER.structures`, reach mode only): every active structure of the
 * system session drawn through the asset contract, with the real look pipeline, so a model swap needs no code change.
 *
 * - **Diffing:** when `session.structuresVersion` changes, `planStructureViews` (pure, plan.ts) decides which views
 *   to create, dispose and keep; kept views rebind to the new structure objects.
 * - **Create:** `runtime.models.get(assetId)` → `instantiate(lodScale)` → `applyStaticLook(lib, instance, look)` →
 *   `enableShadows(root)` → the `structures` group. Relays and lane rings get a relay core (R10) at the `DOCK_TRANSIT`
 *   socket, sized by that socket's `radius` param. Derelict POIs (null `assetId`) are assembled by `buildDerelict`
 *   from the station kit, and their trimesh goes to `session.provideDerelictCollider`.
 * - **Pooling:** released views go to a small free list per (asset id, look) and are reused before instantiating again.
 * - **Per frame** (allocation-free, D-014): each root into render space (D-011) with the structure's rotation; relay
 *   cores tick, and a state change (or reboot progress) re-applies `setState` only.
 * - **Enter hook `structures`:** trims the pool to the new system's assets, instantiates its views ahead of time and
 *   compiles their pipelines in the post pipeline's own passes, every LOD level (`warmPipelines`, R20), so neither the
 *   first frame after the tunnel nor the first close approach hitches. At init (the first system) it is not awaited.
 *
 * Placeholder-agnostic: nothing here reads an entry's `placeholder` flag (asset contract §8.3 rule 3).
 */
import { Group, type Object3D, type Sprite } from 'three/webgpu'
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import type { LoadedModel, ModelInstance } from '../../assets/loader'
import { logger } from '../../core/logger'
import type { Vec3 } from '../../core/math'
import { loadFactionsConfig } from '../../galaxy/layout/config'
import type { SystemLayout } from '../../galaxy/layout/types'
import type { ActiveStructure, ModelSource, RelinkState } from '../../reach/runtime/types'
import { loadSessionConfig } from '../../reach/session/config'
import { structureSites } from '../../reach/session/structures'
import type { FactionId } from '../../render/contracts'
import { buildDerelict, enableShadows } from '../../render/environment'
import { toRenderSpace } from '../../render/floatingOrigin'
import { applyStaticLook, setStaticLightsVisible } from '../../render/materials'
import { loadReachFxConfig, type ReachFxConfig } from '../../render/reachFx/config'
import { createRelayCore, type RelayCore } from '../../render/reachFx/relayCore'
import { warmPipelines } from '../../render/reachFx/warmup'
import { stationKitOf } from '../field/feature'
import { LOOK_BITS, derelictLookStore, lookConfig, type DerelictLookSource } from '../../story-link/reactivation/look'
import type { DerelictLightGroup } from '../../render/environment'
import { loadStructuresConfig, type StructuresConfig } from './config'
import {
  coreStateOf,
  createViewPool,
  hasCore,
  layoutAssetIds,
  planStructureViews,
  poolKey,
  poolKeyAsset,
  preloadList,
  rebootProgress,
  type StructureViewKey,
  type ViewPool,
} from './plan'

/** The socket a relay core sits at (asset contract: the transit aperture's centre, local −Z = transit direction). */
export const CORE_SOCKET = 'DOCK_TRANSIT'

/**
 * M5 C15: a station's light scale on its body: the LIGHT_ socket sprites (or its static light pool) show only while the scale is at least
 * half (a war-disabled station, `lightScale` 0.05, goes dark; the hull materials are shared per look and stay).
 * Runs on each view sync (a rare event), so a pooled body always takes its current structure's scale.
 */
export function applyLightScale(instance: (Pick<ModelInstance, 'sockets'> & { readonly root?: Object3D }) | null, scale: number | undefined): void {
  if (instance === null) return
  setStaticLightsVisible(instance, (scale ?? 1) >= 0.5)
}

/** The drawable part of a view: pooled and reused across structures of the same (asset id, look). */
export interface ViewBody {
  /** The free list it returns to; null = never pooled (derelicts). */
  readonly poolKey: string | null
  readonly root: Object3D
  /** The model instance (null for a derelict build). */
  readonly instance: ModelInstance | null
  /** The model's LOD distances before the quality bias (empty for a derelict). */
  readonly lodDistances: readonly number[]
  readonly core: RelayCore | null
  /** M7 S43: a derelict's look setter (lit windows, beacon, docking light); absent on model bodies. */
  readonly setLook?: (mask: number) => void
}

/** A live view of one active structure. */
export interface StructureView extends StructureViewKey {
  /** The session's structure object (rebound on every diff). */
  structure: ActiveStructure
  readonly body: ViewBody
  /** The core state and progress last applied (−1 = none yet). */
  coreState: number
  coreProgress: number
}

export interface StructuresOptions {
  config?: StructuresConfig
  /** The relay and lane-ring core tuning (default: reachFx.json). */
  fx?: ReachFxConfig
  /** Compiles a preload holder (default `warmPipelines` in the post pipeline's passes; tests compile directly). */
  warm?: (ctx: GameContext, holder: Object3D) => Promise<unknown>
  /** M7 S43: the restarted derelicts' looks (default: the story link's shared store). */
  looks?: DerelictLookSource
}

/** M7 S43: the derelict light groups from `look.json` (derelict-local metres). */
export function derelictLightGroups(): DerelictLightGroup[] {
  const l = lookConfig().lights
  return (Object.keys(LOOK_BITS) as (keyof typeof LOOK_BITS)[]).map((k) => ({ bit: LOOK_BITS[k], color: l[k].color, blinkHz: l[k].blinkHz, positions: l[k].positions }))
}

/** M7 S43: applies the published looks to the derelict views (on a look or view change only). */
export function applyDerelictLooks(views: readonly StructureView[], looks: DerelictLookSource): void {
  for (const v of views) if (v.kind === 'poi' && v.body.setLook !== undefined) v.body.setLook(looks.look(v.id))
}

function warmInPasses(ctx: GameContext, holder: Object3D): Promise<number> {
  return warmPipelines(ctx.renderer, ctx.scene, ctx.camera, holder)
}

/** The feature plus read access for tests and the debug API. */
export interface StructuresFeature extends GameFeature {
  readonly group: Group | null
  readonly views: readonly StructureView[]
  readonly pool: ViewPool<ViewBody>
  /** The session `structuresVersion` the views were last built for (−1 = never). */
  readonly version: number
  frame(ctx: GameContext, f: FrameInfo): void
  /** The enter hook's work, exposed for tests and the debug API: trim the pool, preload and compile. */
  preload(ctx: GameContext, layout: SystemLayout): Promise<void>
  dispose(): void
}

/** A numeric socket param, or null. */
function socketRadius(models: ModelSource, assetId: string): number | null {
  const sockets = models.socketsOf(assetId)
  for (let i = 0; i < sockets.length; i++) {
    if (sockets[i].name !== CORE_SOCKET) continue
    const r = sockets[i].params.radius
    return typeof r === 'number' && r > 0 ? r : null
  }
  return null
}

/** Frees what a body owns beyond the shared model data: the core and the light sprites' materials. */
export function disposeBody(body: ViewBody): void {
  body.core?.dispose()
  body.root.removeFromParent()
  body.root.traverse((o) => {
    const sprite = o as Sprite
    if (sprite.isSprite) sprite.material.dispose()
  })
}

/**
 * Per frame, one core (allocation-free): ticks it and re-applies `setState` when the mapped state changes, or while
 * the relay reboots and its progress moves.
 */
export function updateCore(v: StructureView, relink: RelinkState, dt: number): void {
  const core = v.body.core
  if (core === null) return
  const s = v.structure
  const state = coreStateOf(s.kind, s.state)
  const progress = state === 2 ? rebootProgress(relink, s.index) : 0
  if (state !== v.coreState || progress !== v.coreProgress) {
    core.setState(state, progress)
    v.coreState = state
    v.coreProgress = progress
  }
  core.update(dt)
}

/** Per frame (allocation-free, D-014): every root into render space with its rotation, then its core. */
export function frameStructures(views: readonly StructureView[], cam: Readonly<Vec3>, relink: RelinkState, dt: number): void {
  for (let i = 0; i < views.length; i++) {
    const v = views[i]
    const s = v.structure
    const p = s.position
    const q = s.rotation
    const root = v.body.root
    toRenderSpace(p[0], p[1], p[2], cam, root.position)
    root.quaternion.set(q[0], q[1], q[2], q[3])
    updateCore(v, relink, dt)
  }
}

export function createStructuresFeature(options: StructuresOptions = {}): StructuresFeature {
  const cfg = options.config ?? loadStructuresConfig()
  const fx = options.fx ?? loadReachFxConfig()
  const warm = options.warm ?? warmInPasses
  const looks = options.looks ?? derelictLookStore
  const factions = loadFactionsConfig()
  const sessionCfg = loadSessionConfig()
  const pool = createViewPool<ViewBody>(cfg.poolPerKey)
  let ctxRef: GameContext | null = null
  let group: Group | null = null
  let views: StructureView[] = []
  let version = -1
  let stationKit: Map<string, LoadedModel> | null = null
  let trimmedFor: SystemLayout | null = null
  let offQuality: (() => void) | null = null
  let lightGroups: DerelictLightGroup[] | null = null
  let lookVersion = -1
  const warned = new Set<string>()

  function warnOnce(key: string, message: string): void {
    if (warned.has(key)) return
    warned.add(key)
    logger.warn(message)
  }

  /** Instantiates a view body for `assetId` with `look` (a rare event); null when the model is not loaded. */
  function buildBody(ctx: GameContext, models: ModelSource, kind: ActiveStructure['kind'], assetId: string, look: FactionId): ViewBody | null {
    const model = models.get(assetId)
    if (!model) {
      warnOnce(`model:${assetId}`, `structures: ${assetId} is not loaded; its structures are not drawn`)
      return null
    }
    const instance = model.instantiate(ctx.views.lodScale)
    applyStaticLook(ctx.lib, instance, look)
    enableShadows(instance.root)
    let core: RelayCore | null = null
    if (hasCore(kind)) {
      const socket = instance.sockets.get(CORE_SOCKET)
      const radius = socketRadius(models, assetId)
      if (socket && radius !== null) {
        core = createRelayCore(radius, kind === 'relay' ? fx.relayCore : fx.laneCore)
        socket.add(core.mesh)
      } else {
        warnOnce(`core:${assetId}`, `structures: ${assetId} has no ${CORE_SOCKET} socket with a radius; no relay core`)
      }
    }
    return { poolKey: poolKey(assetId, look), root: instance.root, instance, lodDistances: model.entry.lod.distances, core }
  }

  /** A derelict POI assembled from the station kit, with its collider handed to the session. */
  function buildDerelictBody(ctx: GameContext, s: ActiveStructure): ViewBody | null {
    const reach = ctx.reach
    stationKit ??= stationKitOf(ctx.models)
    if (reach === null || stationKit.size === 0) {
      warnOnce('derelict', 'structures: no kit.station models loaded; derelict POIs are not drawn')
      return null
    }
    lightGroups ??= derelictLightGroups()
    const build = buildDerelict(ctx.lib, stationKit, s.seed, lightGroups)
    enableShadows(build.root)
    if (build.collider.indices.length >= 3) reach.session.provideDerelictCollider(s.id, build.collider, s.position, s.rotation)
    else warnOnce(`derelict-collider`, 'structures: a derelict build has no collider triangles; it stays non-solid')
    return { poolKey: null, root: build.root, instance: null, lodDistances: [], core: null, setLook: build.setLook }
  }

  function releaseBody(body: ViewBody): void {
    body.root.removeFromParent()
    if (body.poolKey === null || !pool.release(body.poolKey, body)) disposeBody(body)
  }

  function createView(ctx: GameContext, models: ModelSource, s: ActiveStructure): StructureView | null {
    let body: ViewBody | null
    if (s.assetId === null) {
      body = s.kind === 'poi' ? buildDerelictBody(ctx, s) : null
      if (s.kind !== 'poi') warnOnce(`noasset:${s.kind}`, `structures: a ${s.kind} without an asset id is not drawn`)
    } else {
      body = pool.acquire(poolKey(s.assetId, s.look)) ?? buildBody(ctx, models, s.kind, s.assetId, s.look)
    }
    if (body === null || group === null) return null
    body.root.visible = true
    group.add(body.root)
    return { kind: s.kind, id: s.id, assetId: s.assetId, look: s.look, structure: s, body, coreState: -1, coreProgress: -1 }
  }

  /** Rebuilds the view list for the session's current structures (a rare event: the version changed). */
  function syncViews(ctx: GameContext): void {
    const reach = ctx.reach
    if (reach === null) return
    const session = reach.session
    const next = session.structures
    const plan = planStructureViews(views, next)
    const kept: StructureView[] = []
    for (const [i, j] of plan.keep) {
      views[i].structure = next[j]
      kept.push(views[i])
    }
    for (const i of plan.dispose) releaseBody(views[i].body)
    for (const j of plan.create) {
      const v = createView(ctx, reach.models, next[j])
      if (v) kept.push(v)
    }
    views = kept
    for (const v of views) if (v.kind === 'station') applyLightScale(v.body.instance, v.structure.lightScale)
    version = session.structuresVersion
    // A new system: the pool keeps only what this one uses (memory stays bounded per system).
    const layout = session.layout
    if (layout !== null && layout !== trimmedFor) trimPool(layout)
  }

  function trimPool(layout: SystemLayout): void {
    const ids = layoutAssetIds(layout)
    pool.trim((key) => ids.has(poolKeyAsset(key)), disposeBody)
    trimmedFor = layout
  }

  async function preload(ctx: GameContext, layout: SystemLayout): Promise<void> {
    const reach = ctx.reach
    if (reach === null || group === null) return
    trimPool(layout)
    const holder = new Group()
    holder.name = 'structures:preload'
    const made: ViewBody[] = []
    for (const item of preloadList(layout, cfg, structureSites(layout, factions, sessionCfg))) {
      const key = poolKey(item.assetId, item.look)
      for (let k = pool.count(key); k < item.count; k++) {
        const body = buildBody(ctx, reach.models, item.kind, item.assetId, item.look)
        if (body === null) break
        holder.add(body.root)
        made.push(body)
      }
    }
    if (made.length > 0 && cfg.warmUp) {
      try {
        // In the post pipeline's own passes, every LOD level (R20: compiled from here they never matched the draw).
        await warm(ctx, holder)
      } catch (err: unknown) {
        logger.warn('structures: pipeline warm-up failed; the views compile on first draw', err)
      }
    }
    for (const body of made) releaseBody(body)
  }

  function rescaleLods(ctx: GameContext): void {
    // Pooled bodies carry the old bias: drop them rather than track them.
    pool.clear(disposeBody)
    const bias = ctx.views.lodScale
    for (const v of views) {
      const inst = v.body.instance
      if (inst === null) continue
      const levels = inst.lod.levels
      const d = v.body.lodDistances
      for (let i = 0; i < levels.length && i < d.length; i++) levels[i].distance = d[i] * bias
    }
  }

  const feature: StructuresFeature = {
    name: 'structures',
    order: FEATURE_ORDER.structures,
    get group(): Group | null {
      return group
    },
    get views(): readonly StructureView[] {
      return views
    },
    pool,
    get version(): number {
      return version
    },

    async init(ctx: GameContext): Promise<void> {
      const reach = ctx.reach
      if (reach === null) return
      ctxRef = ctx
      group = new Group()
      group.name = 'structures'
      ctx.scene.add(group)
      offQuality = ctx.bus.on('quality', () => rescaleLods(ctx))
      reach.session.addEnterHook('structures', (layout: SystemLayout) => (ctxRef === null ? undefined : preload(ctxRef, layout)))
      // The runtime enters its first system before the features initialise (plan §2.5 step 4). Not awaited: the warm-up
      // compiles in the first frames' passes (R20), and the first frame builds its own views meanwhile.
      const current = reach.session.layout
      if (current !== null) void preload(ctx, current)
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      const reach = ctx.reach
      if (group === null || reach === null) return
      if (reach.session.structuresVersion !== version) {
        syncViews(ctx)
        lookVersion = -1
      }
      if (looks.version !== lookVersion) {
        lookVersion = looks.version
        applyDerelictLooks(views, looks)
      }
      frameStructures(views, ctx.rig.pos, reach.relink, f.dt)
    },

    preload,

    dispose(): void {
      offQuality?.()
      offQuality = null
      for (const v of views) disposeBody(v.body)
      views = []
      pool.clear(disposeBody)
      group?.removeFromParent()
      group = null
      version = -1
      trimmedFor = null
      stationKit = null
      ctxRef = null
    },
  }
  return feature
}

const feature: StructuresFeature = createStructuresFeature()
export default feature
